import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ClipboardList,
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Eye,
  Trash2,
  Loader2,
  Package,
  CheckCircle2,
  Clock,
  XCircle,
  Send,
  PackageCheck,
  Mail,
  FileDown,
  Printer,
  Edit2,
  Save,
  Receipt,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  MapPin,
  Truck,
  Ban,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { invokeAuthenticatedRpc } from '@/lib/authenticatedRpc';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useLocations } from '@/hooks/useLocations';
import { useCurrency } from '@/hooks/useCurrency';
import { toast } from 'sonner';
import { subscribeToPurchaseOrders, unsubscribe } from '@/lib/realtime';
import GoodsTransfers from './GoodsTransfers';
import StockReturns from './StockReturns';

interface ShippingAddress {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

interface PurchaseOrder {
  id: string;
  location_id: string;
  supplier_id: string | null;
  supplier_location_id?: string | null;
  order_number: string | null;
  status: 'draft' | 'placed' | 'delivered' | 'partial' | 'received' | 'cancelled' | 'rejected';
  expected_date: string | null;
  received_date: string | null;
  subtotal: number;
  tax: number;
  total: number;
  notes: string | null;
  shipping_address: ShippingAddress | null;
  created_at: string;
  supplier?: { id: string; name: string } | null;
  location?: { id: string; name: string } | null;
  items?: Array<{ quantity_ordered: number }> | null;
}

interface PurchaseOrderItem {
  id: string;
  purchase_order_id: string;
  product_id: string | null;
  description: string | null;
  quantity_ordered: number;
  quantity_received: number;
  unit_cost: number;
  product?: { id: string; name: string; sku: string | null } | null;
}

interface Supplier {
  id: string;
  name: string;
  email?: string | null;
}

interface ProductWithSupplier {
  id: string;
  name: string;
  sku: string | null;
  cost: number | null;
  supplier_id: string | null;
}

interface ProductLocationAvailability {
  product_id: string;
  product_name: string;
  sku: string | null;
  quantity_on_hand: number;
  quantity_reserved: number;
  quantity_available: number;
  quantity_boh: number;
  quantity_foh: number;
}

const formatQuantity = (value: number) => value.toLocaleString(undefined, {
  maximumFractionDigits: 3,
});

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: Clock },
  placed: { label: 'Placed', color: 'bg-blue-100 text-blue-700', icon: Send },
  delivered: { label: 'Delivered', color: 'bg-purple-100 text-purple-700', icon: Package },
  partial: { label: 'Partial', color: 'bg-amber-100 text-amber-700', icon: Truck },
  received: { label: 'Received', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: Ban },
};

export default function PurchaseOrders() {
  const { organization } = useAuth();
  const { locations } = useLocations();
  const { formatCurrency } = useCurrency();
  const queryClient = useQueryClient();
  // Real-time subscription for purchase orders
  useEffect(() => {
    if (!organization?.id) return;
    const channel = subscribeToPurchaseOrders(organization.id, () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['pending-purchase-orders'] });
    });
    return () => { unsubscribe(channel); };
  }, [organization?.id, queryClient]);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [supplierFilter, setSupplierFilter] = useState<string>('all'); // 'all' | 'no_supplier' | `supplier:${id}` | `internal:${locationId}`
  const [deliverToFilter, setDeliverToFilter] = useState<string>('all'); // 'all' | location_id
  const [createdFrom, setCreatedFrom] = useState<string>(''); // YYYY-MM-DD
  const [createdTo, setCreatedTo] = useState<string>(''); // YYYY-MM-DD
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertInvoiceNumber, setConvertInvoiceNumber] = useState('');
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [selectedPOItems, setSelectedPOItems] = useState<PurchaseOrderItem[]>([]);
  const [emailRecipient, setEmailRecipient] = useState('');
  
  // Form state
  const [formData, setFormData] = useState<{
    order_from_type: 'supplier' | 'internal_location';
    location_ids: string[];
    location_id?: string;
    delivery_location_id?: string;
    supplier_id: string;
    supplier_location_id: string;
    expected_date: string;
    notes: string;
    shipping_address: {
      line1: string;
      line2: string;
      city: string;
      state: string;
      postal_code: string;
      country: string;
    };
  }>({
    order_from_type: 'supplier',
    location_ids: [], // Multi-select locations ("Ordering for")
    delivery_location_id: '', // Where to deliver
    supplier_id: '',
    supplier_location_id: '',
    expected_date: '',
    notes: '',
    shipping_address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: '',
    },
  });
  const [formItems, setFormItems] = useState<{ product_id: string; description: string; quantity: string; unit_cost: string; expanded: boolean }[]>([
    { product_id: '', description: '', quantity: '', unit_cost: '', expanded: false },
  ]);
  const [supplierMismatchWarning, setSupplierMismatchWarning] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Receive quantities state
  const [receiveQuantities, setReceiveQuantities] = useState<Record<string, string>>({});

  // Preview order number state
  const [previewOrderNumber, setPreviewOrderNumber] = useState<string | null>(null);

  // Fetch suppliers (with email for auto-fill)
  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];
      const { data, error } = await supabase
        .from('suppliers')
        .select('id, name, email')
        .eq('organization_id', organization.id)
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data as Supplier[];
    },
    enabled: !!organization?.id,
  });

  // Fetch products with supplier_id for mismatch warnings
  const { data: productsWithSupplier = [] } = useQuery({
    queryKey: ['products-with-supplier', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];
      const { data, error } = await supabase.rpc('get_purchase_order_catalog');
      if (error) throw error;
      return (Array.isArray(data) ? data : []) as ProductWithSupplier[];
    },
    enabled: !!organization?.id,
  });

  // Fetch inventory per location for expandable line items
  const { data: inventoryByProduct = {} } = useQuery({
    queryKey: ['inventory-by-product', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return {};
      const { data, error } = await supabase
        .from('inventory_items')
        .select(`
          product_id,
          quantity_on_hand,
          location:locations(id, name)
        `);
      if (error) throw error;
      
      // Group by product_id
      const grouped: Record<string, Array<{ location_id: string; location_name: string; quantity: number }>> = {};
      (data || []).forEach((item: any) => {
        if (!grouped[item.product_id]) {
          grouped[item.product_id] = [];
        }
        grouped[item.product_id].push({
          location_id: item.location?.id,
          location_name: item.location?.name || 'Unknown',
          quantity: item.quantity_on_hand || 0,
        });
      });
      return grouped;
    },
    enabled: !!organization?.id,
  });

  const internalSourceLocationId = formData.order_from_type === 'internal_location'
    ? formData.supplier_location_id
    : '';
  const {
    data: internalSourceAvailability = [],
    isLoading: internalAvailabilityLoading,
    error: internalAvailabilityError,
  } = useQuery({
    queryKey: ['product-location-availability', internalSourceLocationId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_product_location_availability', {
        p_location_id: internalSourceLocationId,
      });
      if (error) throw error;
      return (Array.isArray(data) ? data : []) as ProductLocationAvailability[];
    },
    enabled: (createDialogOpen || editDialogOpen) && !!internalSourceLocationId,
    staleTime: 15_000,
  });

  const internalAvailabilityByProductId = new Map(
    internalSourceAvailability.map((row) => [row.product_id, row] as const),
  );

  const hasInvalidInternalQuantity = formData.order_from_type === 'internal_location'
    && formItems.some((item) => {
      if (!item.product_id || !item.quantity) return false;
      const quantity = Number(item.quantity);
      const available = internalAvailabilityByProductId.get(item.product_id)?.quantity_available ?? 0;
      return !Number.isFinite(quantity) || quantity <= 0 || quantity > available;
    });

  // Fetch purchase orders
  const { data: purchaseOrders = [], isLoading } = useQuery({
    queryKey: ['purchase-orders'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('purchase_orders')
        .select(`
          *,
          supplier:suppliers(id, name),
          location:locations(id, name),
          items:purchase_order_items(quantity_ordered)
        `)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as PurchaseOrder[];
    },
  });

  // Fetch PO items
  const fetchPOItems = async (poId: string) => {
    const { data, error } = await supabase
      .from('purchase_order_items')
      .select(`
        *,
        product:products(id, name, sku)
      `)
      .eq('purchase_order_id', poId);
    if (error) throw error;
    return data as PurchaseOrderItem[];
  };

  const fetchLinkedTransfer = async (poId: string) => {
    const { data, error } = await supabase
      .from('goods_transfers')
      .select('id, status')
      .eq('purchase_order_id', poId)
      .maybeSingle();
    if (error) throw error;
    return data as { id: string; status: string | null } | null;
  };

  const fetchTransferItems = async (transferId: string) => {
    const { data, error } = await supabase.rpc('get_goods_transfer_items', {
      p_transfer_id: transferId,
    });
    if (error) throw error;
    return (Array.isArray(data) ? data : []) as Array<{
      id: string;
      product_id: string | null;
      quantity_sent: number;
      quantity_received: number | null;
    }>;
  };

  // Generate smart order number: SUPPLIER-LOCATION-####
  const generateOrderNumber = async (supplierName: string, locationNames: string[]): Promise<string> => {
    // Get first 3 letters of supplier name (alphanumeric only)
    const supplierCode = supplierName.replace(/[^A-Za-z]/g, '').substring(0, 3).toUpperCase();
    
    // Get first 3 letters of first location name
    const locationCode = locationNames[0]?.replace(/[^A-Za-z]/g, '').substring(0, 3).toUpperCase() || 'LOC';
    
    const prefix = `${supplierCode}-${locationCode}`;
    
    // Find the last order number with this prefix to increment
    const { data: existingOrders } = await supabase
      .from('purchase_orders')
      .select('order_number')
      .like('order_number', `${prefix}-%`)
      .order('created_at', { ascending: false })
      .limit(1);
    
    let nextNum = 1;
    if (existingOrders && existingOrders.length > 0) {
      const lastNum = existingOrders[0].order_number?.split('-').pop();
      if (lastNum) {
        nextNum = (parseInt(lastNum, 10) || 0) + 1;
      }
    }
    
    return `${prefix}-${nextNum.toString().padStart(4, '0')}`;
  };

  // Update preview order number when supplier or location changes
  useEffect(() => {
    const updatePreview = async () => {
      const sourceName =
        formData.order_from_type === 'supplier'
          ? (formData.supplier_id ? suppliers.find(s => s.id === formData.supplier_id)?.name : null)
          : (formData.supplier_location_id ? locations.find(l => l.id === formData.supplier_location_id)?.name : null);

      if (sourceName && formData.location_ids.length > 0) {
        const locationNames = formData.location_ids.map(id =>
          locations.find(l => l.id === id)?.name || 'LOC'
        );
        const orderNumber = await generateOrderNumber(sourceName, locationNames);
        setPreviewOrderNumber(orderNumber);
      } else {
        setPreviewOrderNumber(null);
      }
    };
    updatePreview();
  }, [
    formData.order_from_type,
    formData.supplier_id,
    formData.supplier_location_id,
    formData.location_ids,
    suppliers,
    locations,
  ]);

  // Create PO mutation
  const createPO = useMutation({
    mutationFn: async () => {
      const locationNames = formData.location_ids.map(id =>
        locations.find(l => l.id === id)?.name || 'LOC'
      );
      const sourceName =
        formData.order_from_type === 'supplier'
          ? suppliers.find(s => s.id === formData.supplier_id)?.name || 'SUP'
          : locations.find(l => l.id === formData.supplier_location_id)?.name || 'LOC';

      // Use the preview order number (which may have been manually edited)
      // or generate a new one if not set
      const poNumber = previewOrderNumber || await generateOrderNumber(sourceName, locationNames);

      // Use first selected location or delivery location as the primary location
      const primaryLocationId = formData.delivery_location_id || formData.location_ids[0];

      if (formData.order_from_type === 'internal_location') {
        if (!formData.supplier_location_id) {
          throw new Error('Please select an internal supplier location');
        }
        if (formData.supplier_location_id === primaryLocationId) {
          throw new Error('Internal supplier location must be different from delivery location');
        }
      }

      // Build shipping address if any field is filled
      const hasShippingAddress = formData.shipping_address.line1 || formData.shipping_address.city;
      const shippingAddress = hasShippingAddress ? {
        line1: formData.shipping_address.line1,
        line2: formData.shipping_address.line2 || undefined,
        city: formData.shipping_address.city,
        state: formData.shipping_address.state,
        postal_code: formData.shipping_address.postal_code,
        country: formData.shipping_address.country,
      } : null;

      const itemsToSave = formItems
        .filter(item => item.product_id && item.quantity)
        .map(item => ({
          product_id: item.product_id,
          description: item.description || null,
          quantity: parseFloat(item.quantity),
          unit_cost: parseFloat(item.unit_cost) || 0,
        }));

      const { data: purchaseOrderId, error } = await supabase.rpc('save_purchase_order_draft', {
        p_purchase_order_id: null,
        p_location_id: primaryLocationId,
        p_supplier_id: formData.order_from_type === 'supplier' ? (formData.supplier_id || null) : null,
        p_supplier_location_id: formData.order_from_type === 'internal_location' ? (formData.supplier_location_id || null) : null,
        p_order_number: poNumber,
        p_expected_date: formData.expected_date || null,
        p_notes: formData.notes ? `${formData.notes}\n\nOrdering for: ${locationNames.join(', ')}` : `Ordering for: ${locationNames.join(', ')}`,
        p_shipping_address: shippingAddress,
        p_items: itemsToSave,
      });
      if (error) throw error;

      return { id: purchaseOrderId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['goods-transfers'] });
      toast.success('Purchase order created');
      setCreateDialogOpen(false);
      resetForm();
    },
    onError: (error: Error) => {
      setFormError(error.message);
      toast.error('Purchase order was not created', { description: error.message });
    },
  });

  // Update PO status mutation
  const updatePOStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase
        .from('purchase_orders')
        .update({ status })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      toast.success('Status updated');
    },
  });

  const shipInternalTransfer = useMutation({
    mutationFn: async (po: PurchaseOrder) => {
      const linkedTransfer = await fetchLinkedTransfer(po.id);
      if (!linkedTransfer) throw new Error('No linked transfer found for this order');

      const transferItems = await fetchTransferItems(linkedTransfer.id);
      const payloadItems = transferItems.map((item) => ({
        item_id: item.id,
        quantity_sent: item.quantity_sent,
      }));

      await invokeAuthenticatedRpc('send_goods_transfer_items', {
        p_transfer_id: linkedTransfer.id,
        p_items: payloadItems,
      });

      const { error: poError } = await supabase
        .from('purchase_orders')
        .update({ status: 'delivered' })
        .eq('id', po.id);
      if (poError) throw poError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['goods-transfers'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['raw-inventory-items'] });
      queryClient.invalidateQueries({ queryKey: ['raw-inventory-stock'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-movements'] });
      toast.success('Order shipped', { description: 'Transfer is now in transit' });
    },
    onError: (error) => {
      toast.error('Error', { description: error.message });
    },
  });

  const cancelPurchaseOrder = useMutation({
    mutationFn: async (po: PurchaseOrder) => {
      if (po.supplier_location_id) {
        const linkedTransfer = await fetchLinkedTransfer(po.id);
        if (linkedTransfer?.status && linkedTransfer.status !== 'pending') {
          throw new Error('This internal order has already been shipped and cannot be cancelled');
        }
        if (linkedTransfer?.id) {
          const { error: transferCancelError } = await supabase
            .from('goods_transfers')
            .update({ status: 'cancelled' })
            .eq('id', linkedTransfer.id);
          if (transferCancelError) throw transferCancelError;
        }
      }

      const { error } = await supabase
        .from('purchase_orders')
        .update({ status: 'cancelled' })
        .eq('id', po.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['goods-transfers'] });
      toast.success('Purchase order cancelled');
    },
    onError: (error) => {
      toast.error('Error', { description: error.message });
    },
  });

  // Receive items mutation - calls database function to update inventory
  const receiveItems = useMutation({
    mutationFn: async ({ poId, items, locationId }: { poId: string; items: { id: string; quantity_received: number }[]; locationId: string }) => {
      const previousStatus = selectedPO?.status;
      const isInternal = !!selectedPO?.supplier_location_id;
      if (isInternal) {
        const linkedTransfer = await fetchLinkedTransfer(poId);
        if (!linkedTransfer) throw new Error('No linked transfer found for this order');
        if (linkedTransfer.status === 'pending') {
          throw new Error('This internal order has not been shipped yet');
        }

        const transferItems = await fetchTransferItems(linkedTransfer.id);
        const transferByProductId = new Map(
          transferItems
            .filter((ti) => !!ti.product_id)
            .map((ti) => [ti.product_id as string, ti] as const)
        );

        const newTotalByProductId = new Map<string, number>();
        const payloadItems: Array<{ item_id: string; quantity_received: number }> = [];
        const poItemUpdates: Array<{ po_item_id: string; quantity_received: number }> = [];

        items.forEach(({ id: poItemId, quantity_received: receivedNow }) => {
          if (receivedNow <= 0) return;

          const poItem = selectedPOItems.find((poi) => poi.id === poItemId);
          const productId = poItem?.product_id;
          if (!productId) return;

          const transferItem = transferByProductId.get(productId);
          if (!transferItem) {
            throw new Error('Missing transfer line item for one or more PO items');
          }

          const existingReceived = transferItem.quantity_received ?? 0;
          const newTotalReceived = existingReceived + receivedNow;

          payloadItems.push({
            item_id: transferItem.id,
            quantity_received: newTotalReceived,
          });
          newTotalByProductId.set(productId, newTotalReceived);
          poItemUpdates.push({ po_item_id: poItemId, quantity_received: newTotalReceived });
        });

        if (payloadItems.length > 0) {
          await invokeAuthenticatedRpc('receive_goods_transfer_items', {
            p_transfer_id: linkedTransfer.id,
            p_items: payloadItems,
          });

          // Keep PO item received quantities in sync with transfer items
          const updateResults = await Promise.all(
            poItemUpdates.map(({ po_item_id, quantity_received }) =>
              supabase
                .from('purchase_order_items')
                .update({ quantity_received })
                .eq('id', po_item_id)
            )
          );
          const updateError = updateResults.find((r) => r.error)?.error;
          if (updateError) throw updateError;
        }

        const totalReceivedFor = (productId: string | null) => {
          if (!productId) return 0;
          if (newTotalByProductId.has(productId)) return newTotalByProductId.get(productId) || 0;
          return transferByProductId.get(productId)?.quantity_received ?? 0;
        };

        const allReceived = selectedPOItems.every((poi) => {
          const totalReceived = totalReceivedFor(poi.product_id);
          return totalReceived >= poi.quantity_ordered;
        });

        const someReceived = selectedPOItems.some((poi) => {
          const totalReceived = totalReceivedFor(poi.product_id);
          return totalReceived > 0;
        });

        const newStatus = allReceived ? 'received' : someReceived ? 'partial' : (selectedPO?.status || 'delivered');
        await supabase
          .from('purchase_orders')
          .update({
            status: newStatus,
            received_date: allReceived ? new Date().toISOString() : null,
          })
          .eq('id', poId);

        if (newStatus === 'partial' && previousStatus !== 'partial') {
          try {
            const { error } = await supabase.functions.invoke('notify-po-partial-receive', {
              body: { purchaseOrderId: poId },
            });
            if (error) {
              // Do not fail the receipt flow if the email fails
              console.error('Partial receive email failed:', error);
            }
          } catch (err) {
            console.error('Partial receive email error:', err);
          }
        }

        return;
      }

      // Call receive_po_item() for each item - this updates inventory automatically
      for (const item of items) {
        if (item.quantity_received > 0) {
          const { error } = await supabase.rpc('receive_po_item', {
            p_po_item_id: item.id,
            p_quantity_received: item.quantity_received,
            p_location_id: locationId,
          });
          if (error) throw error;
        }
      }

      // Check if all items are fully received (existing + new qty being received now)
      const allReceived = selectedPOItems.every(poi => {
        const newQty = parseFloat(receiveQuantities[poi.id] || '0');
        const totalReceived = poi.quantity_received + newQty;
        return totalReceived >= poi.quantity_ordered;
      });

      const someReceived = selectedPOItems.some(poi => {
        const newQty = parseFloat(receiveQuantities[poi.id] || '0');
        const totalReceived = poi.quantity_received + newQty;
        return totalReceived > 0;
      });

      // Update PO status
      const newStatus = allReceived ? 'received' : someReceived ? 'partial' : 'placed';
      await supabase
        .from('purchase_orders')
        .update({ 
          status: newStatus,
          received_date: allReceived ? new Date().toISOString() : null,
        })
        .eq('id', poId);

      if (newStatus === 'partial' && previousStatus !== 'partial') {
        try {
          const { error } = await supabase.functions.invoke('notify-po-partial-receive', {
            body: { purchaseOrderId: poId },
          });
          if (error) {
            // Do not fail the receipt flow if the email fails
            console.error('Partial receive email failed:', error);
          }
        } catch (err) {
          console.error('Partial receive email error:', err);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['goods-transfers'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['raw-inventory-items'] });
      queryClient.invalidateQueries({ queryKey: ['raw-inventory-stock'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-movements'] });
      toast.success('Items received', { description: 'Inventory has been updated' });
      setReceiveDialogOpen(false);
    },
  });

  // Delete PO mutation
  const deletePO = useMutation({
    mutationFn: async (po: PurchaseOrder) => {
      if (po.supplier_location_id) {
        const linkedTransfer = await fetchLinkedTransfer(po.id);
        if (linkedTransfer?.status && linkedTransfer.status !== 'pending') {
          throw new Error('This internal order has already been shipped and cannot be deleted');
        }

        if (linkedTransfer?.id) {
          const { error: deleteTransferItemsError } = await supabase
            .from('goods_transfer_items')
            .delete()
            .eq('transfer_id', linkedTransfer.id);
          if (deleteTransferItemsError) throw deleteTransferItemsError;

          const { error: deleteTransferError } = await supabase
            .from('goods_transfers')
            .delete()
            .eq('id', linkedTransfer.id);
          if (deleteTransferError) throw deleteTransferError;
        }
      }

      const { error } = await supabase.from('purchase_orders').delete().eq('id', po.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      toast.success('Purchase order deleted');
    },
  });

  // Convert PO to Invoice mutation
  const convertToInvoice = useMutation({
    mutationFn: async ({ poId, invoiceNumber }: { poId: string; invoiceNumber?: string }) => {
      const { data, error } = await supabase.rpc('create_invoice_from_po', {
        p_purchase_order_id: poId,
        p_invoice_number: invoiceNumber || null,
        p_notes: null,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['supplier-invoices'] });
      setConvertDialogOpen(false);
      setViewDialogOpen(false);
      setConvertInvoiceNumber('');
      toast.success('Invoice Created', {
        description: 'Purchase order has been converted to a supplier invoice. View it in Stock Control > Invoices.',
      });
    },
    onError: (error) => {
      toast.error('Error', { description: error.message });
    },
  });

  // Update PO mutation (for editing draft/sent orders)
  const updatePO = useMutation({
    mutationFn: async () => {
      if (!selectedPO) throw new Error('No PO selected');

      const isInternal = formData.order_from_type === 'internal_location';

      // Build shipping address if any field is filled
      const hasShippingAddress = formData.shipping_address.line1 || formData.shipping_address.city;
      const shippingAddress = hasShippingAddress ? {
        line1: formData.shipping_address.line1,
        line2: formData.shipping_address.line2 || undefined,
        city: formData.shipping_address.city,
        state: formData.shipping_address.state,
        postal_code: formData.shipping_address.postal_code,
        country: formData.shipping_address.country,
      } : null;

      if (isInternal) {
        if (!formData.supplier_location_id) {
          throw new Error('Please select an internal supplier location');
        }
        if (formData.supplier_location_id === selectedPO.location_id) {
          throw new Error('Internal supplier location must be different from delivery location');
        }
      }

      const itemsToSave = formItems
        .filter(item => item.product_id && item.quantity)
        .map(item => ({
          product_id: item.product_id,
          description: item.description || null,
          quantity: parseFloat(item.quantity),
          unit_cost: parseFloat(item.unit_cost) || 0,
        }));

      const { error } = await supabase.rpc('save_purchase_order_draft', {
        p_purchase_order_id: selectedPO.id,
        p_location_id: selectedPO.location_id,
        p_supplier_id: isInternal ? null : (formData.supplier_id || null),
        p_supplier_location_id: isInternal ? (formData.supplier_location_id || null) : null,
        p_order_number: selectedPO.order_number,
        p_expected_date: formData.expected_date || null,
        p_notes: formData.notes || null,
        p_shipping_address: shippingAddress,
        p_items: itemsToSave,
      });
      if (error) throw error;

      return selectedPO;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      toast.success('Purchase order updated');
      setEditDialogOpen(false);
      setSelectedPO(null);
      resetForm();
    },
    onError: (error: Error) => {
      setFormError(error.message);
      toast.error('Purchase order was not updated', { description: error.message });
    },
  });

  // Generate PDF for purchase order
  const generatePDF = async (po: PurchaseOrder) => {
    const items = await fetchPOItems(po.id);

    // Create print window with QuickBooks-style design
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) {
      toast.error('Please allow popups to download PDF');
      return;
    }

    const internalSupplierName = po.supplier_location_id
      ? locations.find((l) => l.id === po.supplier_location_id)?.name
      : null;
    const supplierName = internalSupplierName || po.supplier?.name || 'No Supplier';
    const orgName = organization?.name || 'Your Company';

    // Build shipping address HTML
    const shipTo = po.shipping_address
      ? `<p><strong>${orgName}</strong></p>
         <p>${po.shipping_address.line1 || ''}</p>
         ${po.shipping_address.line2 ? `<p>${po.shipping_address.line2}</p>` : ''}
         <p>${[po.shipping_address.city, po.shipping_address.state, po.shipping_address.postal_code].filter(Boolean).join(', ')}</p>
         ${po.shipping_address.country ? `<p>${po.shipping_address.country}</p>` : ''}`
      : `<p><strong>${orgName}</strong></p><p>No shipping address specified</p>`;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Purchase Order ${po.order_number}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            padding: 40px;
            color: #333;
            line-height: 1.5;
          }
          .header {
            display: flex;
            justify-content: space-between;
            margin-bottom: 40px;
            padding-bottom: 20px;
            border-bottom: 2px solid #2563eb;
          }
          .company-info h1 {
            font-size: 24px;
            color: #2563eb;
            margin-bottom: 4px;
          }
          .company-info p { font-size: 12px; color: #666; }
          .po-info { text-align: right; }
          .po-info h2 {
            font-size: 28px;
            color: #2563eb;
            margin-bottom: 8px;
          }
          .po-info p { font-size: 12px; color: #666; }
          .meta-section {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 40px;
            margin-bottom: 30px;
          }
          .meta-box h3 {
            font-size: 11px;
            text-transform: uppercase;
            color: #666;
            margin-bottom: 8px;
            letter-spacing: 0.5px;
          }
          .meta-box p { font-size: 14px; margin: 2px 0; }
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
          }
          th {
            background: #f8fafc;
            padding: 12px;
            text-align: left;
            font-size: 11px;
            text-transform: uppercase;
            color: #64748b;
            border-bottom: 2px solid #e2e8f0;
          }
          td {
            padding: 12px;
            border-bottom: 1px solid #e2e8f0;
            font-size: 13px;
          }
          .text-right { text-align: right; }
          .totals {
            margin-top: 20px;
            display: flex;
            justify-content: flex-end;
          }
          .totals-box {
            width: 250px;
          }
          .totals-row {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            border-bottom: 1px solid #e2e8f0;
          }
          .totals-row.total {
            font-size: 16px;
            font-weight: 600;
            border-bottom: 2px solid #2563eb;
            color: #2563eb;
          }
          .notes {
            margin-top: 40px;
            padding: 16px;
            background: #f8fafc;
            border-radius: 8px;
          }
          .notes h3 { font-size: 12px; color: #666; margin-bottom: 8px; }
          .notes p { font-size: 13px; }
          @media print {
            body { padding: 20px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-info">
            <h1>${orgName}</h1>
          </div>
          <div class="po-info">
            <h2>PURCHASE ORDER</h2>
            <p><strong>${po.order_number}</strong></p>
            <p>Date: ${new Date(po.created_at).toLocaleDateString()}</p>
            ${po.expected_date ? `<p>Expected: ${new Date(po.expected_date).toLocaleDateString()}</p>` : ''}
          </div>
        </div>

        <div class="meta-section">
          <div class="meta-box">
            <h3>Vendor</h3>
            <p><strong>${supplierName}</strong></p>
          </div>
          <div class="meta-box">
            <h3>Ship To</h3>
            ${shipTo}
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 40px">#</th>
              <th>Product / Service</th>
              <th>Description</th>
              <th class="text-right" style="width: 80px">Qty</th>
              <th class="text-right" style="width: 100px">Rate</th>
              <th class="text-right" style="width: 100px">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${items.map((item, idx) => `
              <tr>
                <td>${idx + 1}</td>
                <td>${item.product?.name || 'Unknown'}</td>
                <td>${item.description || ''}</td>
                <td class="text-right">${item.quantity_ordered}</td>
                <td class="text-right">${formatCurrency(item.unit_cost)}</td>
                <td class="text-right">${formatCurrency(item.quantity_ordered * item.unit_cost)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="totals">
          <div class="totals-box">
            <div class="totals-row">
              <span>Subtotal</span>
              <span>${formatCurrency(po.subtotal)}</span>
            </div>
            <div class="totals-row">
              <span>Tax</span>
              <span>${formatCurrency(po.tax)}</span>
            </div>
            <div class="totals-row total">
              <span>Total</span>
              <span>${formatCurrency(po.total)}</span>
            </div>
          </div>
        </div>

        ${po.notes ? `
          <div class="notes">
            <h3>Notes</h3>
            <p>${po.notes}</p>
          </div>
        ` : ''}

        <script>
          window.onload = function() {
            window.print();
          }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const exportPOToCSV = async (po: PurchaseOrder) => {
    try {
      const items = await fetchPOItems(po.id);

      const headers = [
        'Order #',
        'Supplier',
        'Deliver to',
        'Created',
        'Product',
        'SKU',
        'Description',
        'Qty ordered',
        'Qty received',
        'Unit cost',
        'Amount',
      ];

      const rows: (string | number)[][] = items.map((item) => {
        const qtyOrdered = item.quantity_ordered || 0;
        const unitCost = item.unit_cost || 0;
        const qtyReceived = item.quantity_received || 0;

        return [
          po.order_number || po.id,
          po.supplier?.name || '',
          po.location?.name || '',
          new Date(po.created_at).toLocaleDateString(),
          item.product?.name || '',
          item.product?.sku || '',
          item.description || '',
          qtyOrdered,
          qtyReceived,
          unitCost,
          qtyOrdered * unitCost,
        ];
      });

      const csvContent = [
        headers.join(','),
        ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const safeOrderNumber = (po.order_number || po.id).replace(/[^a-z0-9-_]+/gi, '_');
      a.download = `purchase-order-${safeOrderNumber}-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast.error('Failed to export CSV', { description: err?.message || 'Please try again.' });
    }
  };

  // Send PO email mutation
  const sendPOEmail = useMutation({
    mutationFn: async ({ poId, recipient }: { poId: string; recipient?: string }) => {
      const { data, error } = await supabase.functions.invoke('send-purchase-order', {
        body: {
          purchaseOrderId: poId,
          recipientEmail: recipient,
          organizationName: organization?.name,
        },
      });
      if (error) throw error;
      if (!data.success) throw new Error(data.error || 'Failed to send email');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      toast.success('Purchase order sent', { description: 'Email sent to supplier' });
      setEmailDialogOpen(false);
      setEmailRecipient('');
    },
    onError: (error: Error) => {
      toast.error('Failed to send email', { description: error.message });
    },
  });

  // Open email dialog
  const openEmailDialog = (po: PurchaseOrder) => {
    setSelectedPO(po);
    // Pre-fill with supplier email if available
    const supplierData = suppliers.find(s => s.id === po.supplier_id);
    setEmailRecipient((supplierData as any)?.email || '');
    setEmailDialogOpen(true);
  };

  // Open edit dialog
  const openEditDialog = async (po: PurchaseOrder) => {
    setSelectedPO(po);
    const items = await fetchPOItems(po.id);
    setSelectedPOItems(items);

    // Populate form with existing PO data
    setFormData({
      order_from_type: po.supplier_location_id ? 'internal_location' : 'supplier',
      location_ids: [po.location_id], // Use existing location_id as single selection for edit
      location_id: po.location_id,
      delivery_location_id: po.location_id,
      supplier_id: po.supplier_id || '',
      supplier_location_id: po.supplier_location_id || '',
      expected_date: po.expected_date ? po.expected_date.split('T')[0] : '',
      notes: po.notes || '',
      shipping_address: {
        line1: po.shipping_address?.line1 || '',
        line2: po.shipping_address?.line2 || '',
        city: po.shipping_address?.city || '',
        state: po.shipping_address?.state || '',
        postal_code: po.shipping_address?.postal_code || '',
        country: po.shipping_address?.country || '',
      },
    });

    // Populate form items
    if (items.length > 0) {
      setFormItems(items.map(item => ({
        product_id: item.product_id || '',
        description: item.description || '',
        quantity: item.quantity_ordered.toString(),
        unit_cost: item.unit_cost.toString(),
        expanded: false,
      })));
    } else {
      setFormItems([{ product_id: '', description: '', quantity: '', unit_cost: '', expanded: false }]);
    }

    setEditDialogOpen(true);
  };

  // Filter purchase orders
  const filteredPOs = purchaseOrders.filter(po => {
    if (statusFilter !== 'all' && po.status !== statusFilter) return false;
    if (supplierFilter !== 'all') {
      if (supplierFilter === 'no_supplier') {
        if (po.supplier_id || po.supplier_location_id) return false;
      } else if (supplierFilter.startsWith('supplier:')) {
        const supplierId = supplierFilter.slice('supplier:'.length);
        if (po.supplier_id !== supplierId) return false;
      } else if (supplierFilter.startsWith('internal:')) {
        const fromLocationId = supplierFilter.slice('internal:'.length);
        if (po.supplier_location_id !== fromLocationId) return false;
      }
    }
    if (deliverToFilter !== 'all' && po.location_id !== deliverToFilter) return false;

    if (createdFrom) {
      const from = new Date(createdFrom);
      from.setHours(0, 0, 0, 0);
      if (new Date(po.created_at) < from) return false;
    }
    if (createdTo) {
      const to = new Date(createdTo);
      to.setHours(23, 59, 59, 999);
      if (new Date(po.created_at) > to) return false;
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const supplierName = po.supplier?.name || '';
      const internalSupplierName = po.supplier_location_id
        ? locations.find(l => l.id === po.supplier_location_id)?.name || ''
        : '';
      const locationName = po.location?.name || '';
      return (
        po.order_number?.toLowerCase().includes(query) ||
        supplierName.toLowerCase().includes(query) ||
        internalSupplierName.toLowerCase().includes(query) ||
        locationName.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const handleCreatePO = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (formData.location_ids.length === 0) {
      setFormError('Please select at least one location you are ordering for');
      return;
    }

    const primaryLocationId = formData.delivery_location_id || formData.location_ids[0];
    if (formData.order_from_type === 'internal_location') {
      if (!formData.supplier_location_id) {
        setFormError('Please select an internal supplier location');
        return;
      }
      if (formData.supplier_location_id === primaryLocationId) {
        setFormError('Internal supplier location must be different from delivery location');
        return;
      }
      if (internalAvailabilityLoading) {
        setFormError('Wait for source inventory availability to finish loading');
        return;
      }
      if (internalAvailabilityError) {
        setFormError('Source inventory availability could not be loaded. Refresh and try again.');
        return;
      }
      if (hasInvalidInternalQuantity) {
        setFormError('One or more quantities exceed the stock available at the internal source location');
        return;
      }
    }

    const validItems = formItems.filter(item => item.product_id && item.quantity);
    if (validItems.length === 0) {
      setFormError('Please add at least one item');
      return;
    }

    await createPO.mutateAsync();
  };

  const handleUpdatePO = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (formData.order_from_type === 'internal_location') {
      if (internalAvailabilityLoading) {
        setFormError('Wait for source inventory availability to finish loading');
        return;
      }
      if (internalAvailabilityError) {
        setFormError('Source inventory availability could not be loaded. Refresh and try again.');
        return;
      }
      if (hasInvalidInternalQuantity) {
        setFormError('One or more quantities exceed the stock available at the internal source location');
        return;
      }
    }
    updatePO.mutate();
  };

  const handleReceiveItems = async () => {
    if (!selectedPO) return;

    const itemsToReceive = selectedPOItems
      .filter(poi => receiveQuantities[poi.id])
      .map(poi => ({
        id: poi.id,
        quantity_received: parseFloat(receiveQuantities[poi.id]) || 0,
      }));

    await receiveItems.mutateAsync({ 
      poId: selectedPO.id, 
      items: itemsToReceive,
      locationId: selectedPO.location_id,
    });
  };

  const openViewDialog = async (po: PurchaseOrder) => {
    setSelectedPO(po);
    const items = await fetchPOItems(po.id);
    setSelectedPOItems(items);
    setViewDialogOpen(true);
  };

  const openReceiveDialog = async (po: PurchaseOrder) => {
    setSelectedPO(po);
    const items = await fetchPOItems(po.id);
    setSelectedPOItems(items);
    // Initialize receive quantities to empty (user enters NEW qty being received now)
    const initialQuantities: Record<string, string> = {};
    items.forEach(item => {
      initialQuantities[item.id] = '';
    });
    setReceiveQuantities(initialQuantities);
    setReceiveDialogOpen(true);
  };

  const addFormItem = () => {
    setFormItems([...formItems, { product_id: '', description: '', quantity: '', unit_cost: '', expanded: false }]);
  };

  const removeFormItem = (index: number) => {
    setFormItems(formItems.filter((_, i) => i !== index));
    // Re-check supplier mismatch warnings after removing an item
    checkSupplierMismatch(formItems.filter((_, i) => i !== index));
  };

  const toggleItemExpanded = (index: number) => {
    const newItems = [...formItems];
    newItems[index] = { ...newItems[index], expanded: !newItems[index].expanded };
    setFormItems(newItems);
  };

  // Get address object from location
  const getLocationAddressObject = (locationId: string): { line1: string; line2: string; city: string; state: string; postal_code: string; country: string } => {
    const location = locations.find(l => l.id === locationId);
    if (!location?.address) {
      return { line1: '', line2: '', city: '', state: '', postal_code: '', country: '' };
    }

    const addr = location.address as any;
    return {
      line1: addr.line1 || addr.street || '',
      line2: addr.line2 || '',
      city: addr.city || '',
      state: addr.state || addr.parish || '',
      postal_code: addr.postal_code || '',
      country: addr.country || '',
    };
  };

  // Update shipping address when delivery location changes
  const handleDeliveryLocationChange = (locationId: string) => {
    const address = getLocationAddressObject(locationId);
    setFormData({
      ...formData,
      delivery_location_id: locationId,
      shipping_address: address,
    });
  };

  // Check for supplier mismatch when adding products
  const checkSupplierMismatch = (items: typeof formItems) => {
    if (formData.order_from_type !== 'supplier' || !formData.supplier_id) {
      setSupplierMismatchWarning(null);
      return;
    }

    const mismatchedProducts = items
      .filter(item => {
        if (!item.product_id) return false;
        const product = productsWithSupplier.find(p => p.id === item.product_id);
        return product?.supplier_id && product.supplier_id !== formData.supplier_id;
      })
      .map(item => {
        const product = productsWithSupplier.find(p => p.id === item.product_id);
        const productSupplier = suppliers.find(s => s.id === product?.supplier_id);
        return `${product?.name} (usually from ${productSupplier?.name || 'another supplier'})`;
      });
    
    if (mismatchedProducts.length > 0) {
      setSupplierMismatchWarning(
        `Warning: ${mismatchedProducts.join(', ')} ${mismatchedProducts.length === 1 ? 'is' : 'are'} usually ordered from a different supplier.`
      );
    } else {
      setSupplierMismatchWarning(null);
    }
  };

  const updateFormItem = (index: number, field: string, value: string) => {
    const newItems = [...formItems];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Auto-populate cost from product when product is selected
    if (field === 'product_id' && value) {
      const product = productsWithSupplier.find(p => p.id === value);
      if (product?.cost != null) {
        newItems[index].unit_cost = product.cost.toString();
      }
      
      // Check for supplier mismatch
      checkSupplierMismatch(newItems);
    }
    
    setFormItems(newItems);
  };

  const resetForm = () => {
    setFormData({
      order_from_type: 'supplier',
      location_ids: [],
      delivery_location_id: '',
      supplier_id: '',
      supplier_location_id: '',
      expected_date: '',
      notes: '',
      shipping_address: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
      },
    });
    setFormItems([{ product_id: '', description: '', quantity: '', unit_cost: '', expanded: false }]);
    setFormError(null);
    setSupplierMismatchWarning(null);
    setPreviewOrderNumber(null);
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'orders';
  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value }, { replace: true });
  };

  return (
    <div>
      {/* Page title */}
      <div className="mb-4">
        <h2 className="text-lg font-semibold">Stock Control</h2>
        <p className="text-sm text-muted-foreground">
          Create and manage stock orders, transfers, and returns
        </p>
      </div>

      {/* Tabs: Orders / Transfers / Returns */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="mb-6 border-b w-full justify-start rounded-none bg-transparent p-0">
          <TabsTrigger
            value="orders"
            className="rounded-none border-b-2 border-transparent px-4 pb-3 pt-2 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
          >
            Orders
          </TabsTrigger>
          <TabsTrigger
            value="transfers"
            className="rounded-none border-b-2 border-transparent px-4 pb-3 pt-2 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
          >
            Transfers
          </TabsTrigger>
          <TabsTrigger
            value="returns"
            className="rounded-none border-b-2 border-transparent px-4 pb-3 pt-2 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
          >
            Returns
          </TabsTrigger>
        </TabsList>

        {/* Orders Tab */}
        <TabsContent value="orders" className="mt-0">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-base font-medium">Purchase Orders</h3>
        </div>
        <Button variant="blaze" className="gap-2" onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4" />
          Create Order
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4 mb-6">
        <div className="flex-1 min-w-[200px] max-w-sm">
          <Label className="mb-1.5 block">Search</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Order #, supplier, location"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        <div className="min-w-[150px]">
          <Label className="mb-1.5 block">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger>
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="placed">Placed</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="received">Received</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="min-w-[200px]">
          <Label className="mb-1.5 block">Supplier</Label>
          <Select value={supplierFilter} onValueChange={setSupplierFilter}>
            <SelectTrigger>
              <SelectValue placeholder="All suppliers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All suppliers</SelectItem>
              <SelectItem value="no_supplier">No supplier</SelectItem>
              {suppliers.length > 0 && <div className="my-1 h-px bg-border" />}
              {suppliers.map((s) => (
                <SelectItem key={s.id} value={`supplier:${s.id}`}>
                  {s.name}
                </SelectItem>
              ))}
              {locations.length > 0 && <div className="my-1 h-px bg-border" />}
              {locations.map((loc) => (
                <SelectItem key={loc.id} value={`internal:${loc.id}`}>
                  Internal: {loc.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="min-w-[160px]">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="gap-2 w-full justify-start">
                <Filter className="h-4 w-4" />
                More filters
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-96">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                    Deliver to
                  </Label>
                  <Select value={deliverToFilter} onValueChange={setDeliverToFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="All locations" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All locations</SelectItem>
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                      Created from
                    </Label>
                    <Input
                      type="date"
                      value={createdFrom}
                      onChange={(e) => setCreatedFrom(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                      Created to
                    </Label>
                    <Input
                      type="date"
                      value={createdTo}
                      onChange={(e) => setCreatedTo(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setDeliverToFilter('all');
                      setCreatedFrom('');
                      setCreatedTo('');
                    }}
                    disabled={deliverToFilter === 'all' && !createdFrom && !createdTo}
                  >
                    Clear
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    Filters apply immediately.
                  </span>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Purchase Orders Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading purchase orders...</span>
          </div>
        </div>
      ) : filteredPOs.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <ClipboardList className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-medium mb-2">No purchase orders</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create your first purchase order to track inventory orders
              </p>
              <Button variant="blaze" className="gap-2" onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4" />
                New Purchase Order
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/50 border-b">
              <tr>
                <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                  Order #
                </th>
                <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                  Supplier
                </th>
                <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                  Deliver to
                </th>
                <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                  Created
                </th>
                <th className="text-right text-sm font-medium text-muted-foreground px-4 py-3">
                  Total qty
                </th>
                <th className="text-right text-sm font-medium text-muted-foreground px-4 py-3">
                  Total Cost
                </th>
                <th className="text-center text-sm font-medium text-muted-foreground px-4 py-3">
                  Status
                </th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredPOs.map((po) => {
                const statusConfig = STATUS_CONFIG[po.status];
                const StatusIcon = statusConfig.icon;
                const totalQty = (po.items || []).reduce((sum, item) => sum + (item.quantity_ordered || 0), 0);
                const internalSupplierName = po.supplier_location_id
                  ? locations.find(l => l.id === po.supplier_location_id)?.name
                  : null;
                const isInternal = !!po.supplier_location_id;
                return (
                  <tr key={po.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <button
                        onClick={() => openViewDialog(po)}
                        className="font-medium text-sm hover:underline"
                      >
                        {po.order_number || 'Draft'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {internalSupplierName ? (
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="px-2 py-0.5 text-xs">
                            Internal
                          </Badge>
                          <span className="font-medium">{internalSupplierName}</span>
                        </div>
                      ) : (
                        po.supplier?.name || <span className="text-muted-foreground">No supplier</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {po.location?.name}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {new Date(po.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right font-medium tabular-nums">
                      {totalQty.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right font-medium tabular-nums">
                      {formatCurrency(po.total)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge className={cn('gap-1', statusConfig.color)}>
                        <StatusIcon className="h-3 w-3" />
                        {statusConfig.label}
                      </Badge>
                    </td>
                    <td className="px-2 py-3">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openViewDialog(po)}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          {(po.status === 'draft' || po.status === 'placed') && (
                            <DropdownMenuItem onClick={() => openEditDialog(po)}>
                              <Edit2 className="h-4 w-4 mr-2" />
                              Edit Order
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => generatePDF(po)}>
                            <Printer className="h-4 w-4 mr-2" />
                            Print
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => exportPOToCSV(po)}>
                            <FileDown className="h-4 w-4 mr-2" />
                            Export CSV
                          </DropdownMenuItem>
                          {isInternal && (po.status === 'draft' || po.status === 'placed') && (
                            <DropdownMenuItem onClick={() => shipInternalTransfer.mutateAsync(po)}>
                              <Truck className="h-4 w-4 mr-2" />
                              Ship
                            </DropdownMenuItem>
                          )}
                          {!isInternal && po.status === 'draft' && (
                            <DropdownMenuItem
                              onClick={() => updatePOStatus.mutateAsync({ id: po.id, status: 'placed' })}
                            >
                              <Send className="h-4 w-4 mr-2" />
                              Mark as Placed
                            </DropdownMenuItem>
                          )}
                          {!isInternal && (po.status === 'draft' || po.status === 'placed') && (
                            <DropdownMenuItem onClick={() => openEmailDialog(po)}>
                              <Mail className="h-4 w-4 mr-2" />
                              Email to Supplier
                            </DropdownMenuItem>
                          )}
                          {!isInternal && po.status === 'placed' && (
                            <DropdownMenuItem
                              onClick={() => updatePOStatus.mutateAsync({ id: po.id, status: 'delivered' })}
                            >
                              <Truck className="h-4 w-4 mr-2" />
                              Mark as Delivered
                            </DropdownMenuItem>
                          )}
                          {((!isInternal && (po.status === 'placed' || po.status === 'delivered' || po.status === 'partial')) ||
                            (isInternal && (po.status === 'delivered' || po.status === 'partial'))) && (
                            <DropdownMenuItem onClick={() => openReceiveDialog(po)}>
                              <PackageCheck className="h-4 w-4 mr-2" />
                              Receive Items
                            </DropdownMenuItem>
                          )}
                          {!isInternal && (po.status === 'partial' || po.status === 'received') && (
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedPO(po);
                                setConvertDialogOpen(true);
                              }}
                            >
                              <Receipt className="h-4 w-4 mr-2" />
                              Convert to Invoice
                            </DropdownMenuItem>
                          )}
                          {((!isInternal && po.status !== 'received' && po.status !== 'cancelled' && po.status !== 'rejected') ||
                            (isInternal && (po.status === 'draft' || po.status === 'placed'))) && (
                            <DropdownMenuItem
                              onClick={() => {
                                if (confirm('Cancel this purchase order?')) {
                                  cancelPurchaseOrder.mutateAsync(po);
                                }
                              }}
                              className="text-red-600"
                            >
                              <Ban className="h-4 w-4 mr-2" />
                              Cancel
                            </DropdownMenuItem>
                          )}
                          {po.status === 'draft' && (
                            <DropdownMenuItem
                              onClick={() => {
                                if (confirm('Delete this purchase order?')) {
                                  deletePO.mutateAsync(po);
                                }
                              }}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
        </TabsContent>

        {/* Transfers Tab */}
        <TabsContent value="transfers" className="mt-0">
          <GoodsTransfers />
        </TabsContent>

        {/* Returns Tab */}
        <TabsContent value="returns" className="mt-0">
          <StockReturns />
        </TabsContent>
      </Tabs>

      {/* Create PO Dialog - QuickBooks Style */}
      <ResponsiveModal open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <ResponsiveModalContent maxWidth="5xl">
          <form onSubmit={handleCreatePO}>
            {/* Header */}
            <div className="shrink-0 border-b px-6 py-4">
              <div className="flex items-center gap-3">
                <ClipboardList className="h-6 w-6 text-muted-foreground" />
                <div>
                  <h2 className="text-xl font-semibold">New Purchase Order</h2>
                  <p className="text-sm text-muted-foreground">Create a purchase order to order inventory from a supplier or another location</p>
                </div>
              </div>
            </div>

            <ResponsiveModalBody className="max-h-[70vh] overflow-y-auto">
              <div className="space-y-6">
                {formError && (
                  <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-950/20 rounded-lg">
                    {formError}
                  </div>
                )}

                {supplierMismatchWarning && (
                  <div className="p-3 text-sm text-amber-700 bg-amber-50 dark:bg-amber-950/20 dark:text-amber-400 rounded-lg flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>{supplierMismatchWarning}</span>
                  </div>
                )}

                {/* Info Grid - QuickBooks style */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-6 p-4 bg-muted/30 rounded-lg">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide">Order from *</Label>
                    <Select
                      value={formData.order_from_type}
                      onValueChange={(value) => {
                        setFormData({
                          ...formData,
                          order_from_type: value as 'supplier' | 'internal_location',
                          supplier_id: '',
                          supplier_location_id: '',
                        });
                        setSupplierMismatchWarning(null);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choose source" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="supplier">External supplier</SelectItem>
                        <SelectItem value="internal_location">Internal location</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                      {formData.order_from_type === 'supplier' ? 'Supplier *' : 'Internal location *'}
                    </Label>
                    {formData.order_from_type === 'supplier' ? (
                      <Select
                        value={formData.supplier_id}
                        onValueChange={(value) => {
                          setFormData({ ...formData, supplier_id: value });
                          // Re-check supplier mismatch when supplier changes
                          setTimeout(() => checkSupplierMismatch(formItems), 0);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select supplier" />
                        </SelectTrigger>
                        <SelectContent>
                          {suppliers.map((supplier) => (
                            <SelectItem key={supplier.id} value={supplier.id}>
                              {supplier.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Select
                        value={formData.supplier_location_id}
                        onValueChange={(value) => {
                          setFormData({ ...formData, supplier_location_id: value });
                          setFormError(null);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select internal location" />
                        </SelectTrigger>
                        <SelectContent>
                          {locations.map((location) => (
                            <SelectItem key={location.id} value={location.id}>
                              {location.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide">Order Date</Label>
                    <Input
                      type="date"
                      value={new Date().toISOString().split('T')[0]}
                      disabled
                      className="bg-muted/50"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide">Expected Delivery</Label>
                    <Input
                      type="date"
                      value={formData.expected_date}
                      onChange={(e) => setFormData({ ...formData, expected_date: e.target.value })}
                    />
                  </div>

                  {/* Order Number Preview */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide">Order # (auto)</Label>
                    <Input
                      value={previewOrderNumber || ''}
                      onChange={(e) => setPreviewOrderNumber(e.target.value)}
                      placeholder="Select source & location"
                      className="font-mono text-sm bg-muted/50"
                    />
                  </div>
                </div>

                {formData.order_from_type === 'internal_location' && formData.supplier_location_id && (
                  <div className={cn(
                    'rounded-md border px-3 py-2 text-sm',
                    internalAvailabilityError
                      ? 'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300'
                      : 'border-border bg-muted/30 text-muted-foreground',
                  )}>
                    {internalAvailabilityLoading
                      ? 'Loading current stock at the internal source location...'
                      : internalAvailabilityError
                        ? 'Source stock could not be loaded. Refresh and try again.'
                        : 'Internal orders create a goods transfer. Available quantities below exclude reserved stock.'}
                  </div>
                )}

                {/* Ordering For (Multi-select locations) */}
                <div className="p-4 bg-muted/30 rounded-lg space-y-3">
                  <Label className="text-sm font-medium">Ordering for *</Label>
                  <p className="text-xs text-muted-foreground -mt-1">Select which locations this order is for</p>
                  <div className="flex flex-wrap gap-2">
                    {locations.map((location) => {
                      const isSelected = formData.location_ids.includes(location.id);
                      return (
                        <button
                          key={location.id}
                          type="button"
                          onClick={() => {
                            const newIds = isSelected
                              ? formData.location_ids.filter(id => id !== location.id)
                              : [...formData.location_ids, location.id];
                            setFormData({ ...formData, location_ids: newIds });
                          }}
                          className={cn(
                            'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                            isSelected
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-background border hover:bg-muted'
                          )}
                        >
                          {location.name}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Delivery Location & Shipping Address */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide">Deliver to</Label>
                    <Select
                      value={formData.delivery_location_id}
                      onValueChange={handleDeliveryLocationChange}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select delivery location" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((location) => (
                          <SelectItem key={location.id} value={location.id}>
                            {location.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                      <MapPin className="h-3 w-3" />
                      Shipping Address
                    </Label>
                    <Input
                      value={formData.shipping_address.line1}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        shipping_address: { ...formData.shipping_address, line1: e.target.value } 
                      })}
                      placeholder="Auto-filled from location, editable if needed"
                      className="text-sm"
                    />
                  </div>
                </div>

                {/* Line Items Table */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-sm font-medium">Line Items</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addFormItem}>
                      <Plus className="h-3 w-3 mr-1" />
                      Add Line
                    </Button>
                  </div>

                  <div className="rounded-lg border overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-muted/50 border-b">
                        <tr>
                          <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3 w-10">
                            
                          </th>
                          <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">
                            Product / Service
                          </th>
                          <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">
                            Description
                          </th>
                          <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3 w-24">
                            Qty
                          </th>
                          <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3 w-28">
                            Cost Price
                          </th>
                          <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3 w-28">
                            Amount
                          </th>
                          <th className="w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {formItems.map((item, index) => {
                          const qty = parseFloat(item.quantity) || 0;
                          const rate = parseFloat(item.unit_cost) || 0;
                          const amount = qty * rate;
                          const productInventory = item.product_id ? inventoryByProduct[item.product_id] || [] : [];
                          const sourceAvailability = internalAvailabilityByProductId.get(item.product_id);
                          const exceedsSourceAvailability = formData.order_from_type === 'internal_location'
                            && !!item.product_id
                            && !!item.quantity
                            && qty > (sourceAvailability?.quantity_available ?? 0);
                          return (
                            <React.Fragment key={index}>
                              <tr className={cn(
                                'hover:bg-muted/20',
                                exceedsSourceAvailability && 'bg-red-500/5',
                              )}>
                                <td className="px-4 py-2 text-sm text-muted-foreground">
                                  {item.product_id && (
                                    <button
                                      type="button"
                                      onClick={() => toggleItemExpanded(index)}
                                      className="p-1 hover:bg-muted rounded"
                                    >
                                      {item.expanded ? (
                                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                      )}
                                    </button>
                                  )}
                                </td>
                                <td className="px-4 py-2">
                                  <Select
                                    value={item.product_id}
                                    onValueChange={(value) => updateFormItem(index, 'product_id', value)}
                                  >
                                    <SelectTrigger className="border-0 bg-transparent shadow-none h-8 px-2 focus:ring-1">
                                      <SelectValue placeholder="Select product" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {productsWithSupplier.map((product) => {
                                        const availability = internalAvailabilityByProductId.get(product.id);
                                        return (
                                        <SelectItem
                                          key={product.id}
                                          value={product.id}
                                          disabled={
                                            formData.order_from_type === 'internal_location'
                                            && !!formData.supplier_location_id
                                            && (availability?.quantity_available ?? 0) <= 0
                                            && item.product_id !== product.id
                                          }
                                        >
                                          <div className="flex items-center gap-2">
                                            <span>{product.name}</span>
                                            {product.sku && (
                                              <span className="text-xs text-muted-foreground">({product.sku})</span>
                                            )}
                                            {formData.order_from_type === 'internal_location' && (
                                              <span className="text-xs text-muted-foreground">
                                                {formatQuantity(availability?.quantity_available ?? 0)} available
                                              </span>
                                            )}
                                          </div>
                                        </SelectItem>
                                        );
                                      })}
                                    </SelectContent>
                                  </Select>
                                  {formData.order_from_type === 'internal_location' && item.product_id && (
                                    <p className={cn(
                                      'mt-1 text-xs',
                                      exceedsSourceAvailability ? 'text-red-700 dark:text-red-300' : 'text-muted-foreground',
                                    )}>
                                      {formatQuantity(sourceAvailability?.quantity_available ?? 0)} available at source
                                      {(sourceAvailability?.quantity_reserved ?? 0) > 0
                                        ? ` · ${formatQuantity(sourceAvailability?.quantity_reserved ?? 0)} reserved`
                                        : ''}
                                    </p>
                                  )}
                                </td>
                                <td className="px-4 py-2">
                                  <Input
                                    placeholder="Description"
                                    value={item.description}
                                    onChange={(e) => updateFormItem(index, 'description', e.target.value)}
                                    className="border-0 bg-transparent shadow-none h-8 px-2 focus:ring-1"
                                  />
                                </td>
                                <td className="px-4 py-2">
                                  <Input
                                    type="number"
                                    min="0.001"
                                    max={formData.order_from_type === 'internal_location'
                                      ? sourceAvailability?.quantity_available
                                      : undefined}
                                    step="any"
                                    placeholder="0"
                                    value={item.quantity}
                                    onChange={(e) => updateFormItem(index, 'quantity', e.target.value)}
                                    className="border-0 bg-transparent shadow-none h-8 px-2 text-right focus:ring-1"
                                    aria-invalid={exceedsSourceAvailability}
                                  />
                                </td>
                                <td className="px-4 py-2">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder="0.00"
                                    value={item.unit_cost}
                                    onChange={(e) => updateFormItem(index, 'unit_cost', e.target.value)}
                                    className="border-0 bg-transparent shadow-none h-8 px-2 text-right focus:ring-1"
                                  />
                                </td>
                                <td className="px-4 py-2 text-right font-medium tabular-nums text-sm">
                                  {formatCurrency(amount)}
                                </td>
                                <td className="px-2 py-2">
                                  {formItems.length > 1 && (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7 text-muted-foreground hover:text-red-600"
                                      onClick={() => removeFormItem(index)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                </td>
                              </tr>
                              {/* Expanded Inventory View */}
                              {item.expanded && item.product_id && (
                                <tr className="bg-muted/30">
                                  <td colSpan={7} className="px-4 py-3">
                                    <div className="text-xs font-medium text-muted-foreground mb-2">
                                      Inventory by Location
                                    </div>
                                    <div className="rounded-md border bg-background overflow-hidden">
                                      <table className="w-full text-xs">
                                        <thead className="bg-muted/50">
                                          <tr>
                                            <th className="text-left px-3 py-1.5 font-medium">Outlet</th>
                                            <th className="text-right px-3 py-1.5 font-medium">Current inventory</th>
                                            <th className="text-right px-3 py-1.5 font-medium">Inbound inventory</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y">
                                          {productInventory.length > 0 ? (
                                            productInventory.map((inv, invIdx) => (
                                              <tr key={invIdx} className="hover:bg-muted/30">
                                                <td className="px-3 py-1.5">{inv.location_name}</td>
                                                <td className="px-3 py-1.5 text-right tabular-nums">{inv.quantity}</td>
                                                <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">0</td>
                                              </tr>
                                            ))
                                          ) : (
                                            <tr>
                                              <td colSpan={3} className="px-3 py-2 text-center text-muted-foreground">
                                                No inventory records found
                                              </td>
                                            </tr>
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Totals Section */}
                <div className="flex justify-end">
                  <div className="w-72 space-y-2">
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span className="font-medium tabular-nums">
                        {formatCurrency(formItems.reduce((sum, item) => {
                          const qty = parseFloat(item.quantity) || 0;
                          const cost = parseFloat(item.unit_cost) || 0;
                          return sum + qty * cost;
                        }, 0))}
                      </span>
                    </div>
                    <div className="flex justify-between py-3 border-b-2 border-primary">
                      <span className="font-semibold">Total</span>
                      <span className="font-bold text-lg text-primary tabular-nums">
                        {formatCurrency(formItems.reduce((sum, item) => {
                          const qty = parseFloat(item.quantity) || 0;
                          const cost = parseFloat(item.unit_cost) || 0;
                          return sum + qty * cost;
                        }, 0))}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wide">Notes / Memo</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes for this purchase order..."
                    rows={2}
                    className="bg-background"
                  />
                </div>
              </div>
            </ResponsiveModalBody>

            <ResponsiveModalFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="blaze"
                disabled={
                  createPO.isPending
                  || (formData.order_from_type === 'internal_location'
                    && (internalAvailabilityLoading || !!internalAvailabilityError || hasInvalidInternalQuantity))
                }
                className="gap-2"
              >
                {createPO.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                <Save className="h-4 w-4" />
                Save
              </Button>
            </ResponsiveModalFooter>
          </form>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* View PO Dialog - Lightspeed Style */}
      <ResponsiveModal open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <ResponsiveModalContent maxWidth="5xl">
          {/* Header: PO name + (Status) */}
          <div className="shrink-0 border-b px-6 pr-12 py-4">
            <h2 className="text-xl font-semibold">
              {selectedPO?.order_number || 'Purchase Order'}{' '}
              <span className="text-muted-foreground font-normal">
                ({selectedPO ? STATUS_CONFIG[selectedPO.status].label : ''})
              </span>
            </h2>

            {/* Action buttons row */}
            <div className="flex items-center gap-2 mt-3">
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => selectedPO && exportPOToCSV(selectedPO)}
              >
                <FileDown className="h-4 w-4" />
                Export CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => selectedPO && generatePDF(selectedPO)}
              >
                <Printer className="h-4 w-4" />
                Print labels
              </Button>
              {(selectedPO?.status === 'draft' || selectedPO?.status === 'placed') && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => {
                    setViewDialogOpen(false);
                    if (selectedPO) openEditDialog(selectedPO);
                  }}
                >
                  <Edit2 className="h-4 w-4" />
                  Edit prices
                </Button>
              )}
              {!selectedPO?.supplier_location_id && (selectedPO?.status === 'draft' || selectedPO?.status === 'placed') && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => {
                    setViewDialogOpen(false);
                    if (selectedPO) openEmailDialog(selectedPO);
                  }}
                >
                  <Mail className="h-4 w-4" />
                  Email
                </Button>
              )}
              {selectedPO?.supplier_location_id && (selectedPO?.status === 'draft' || selectedPO?.status === 'placed') && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => {
                    if (!selectedPO) return;
                    if (!confirm('Ship this internal order now? This will deduct inventory from the supplying location.')) return;
                    setViewDialogOpen(false);
                    shipInternalTransfer.mutateAsync(selectedPO);
                  }}
                  disabled={shipInternalTransfer.isPending}
                >
                  {shipInternalTransfer.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  <Truck className="h-4 w-4" />
                  Ship
                </Button>
              )}
              {((!selectedPO?.supplier_location_id &&
                (selectedPO?.status === 'placed' || selectedPO?.status === 'delivered' || selectedPO?.status === 'partial')) ||
                (selectedPO?.supplier_location_id &&
                  (selectedPO?.status === 'delivered' || selectedPO?.status === 'partial'))) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => {
                    setViewDialogOpen(false);
                    if (selectedPO) openReceiveDialog(selectedPO);
                  }}
                >
                  <PackageCheck className="h-4 w-4" />
                  Receive
                </Button>
              )}
              {!selectedPO?.supplier_location_id && (selectedPO?.status === 'partial' || selectedPO?.status === 'received') && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => setConvertDialogOpen(true)}
                >
                  <Receipt className="h-4 w-4" />
                  Convert to Invoice
                </Button>
              )}
            </div>
          </div>

          <ResponsiveModalBody className="max-h-[70vh] overflow-y-auto">
            <div className="space-y-6">
              {/* Two-column info section - Lightspeed style */}
              <div className="grid grid-cols-2 gap-x-12 gap-y-3 py-4">
                {/* Left column */}
                <div className="space-y-3">
                  <div className="flex">
                    <span className="w-40 font-semibold text-sm">Deliver to</span>
                    <span className="text-sm">{selectedPO?.location?.name || '-'}</span>
                  </div>
                  <div className="flex">
                    <span className="w-40 font-semibold text-sm">
                      {selectedPO?.supplier_location_id ? 'From location' : 'Supplier'}
                    </span>
                    <span className="text-sm">
                      {selectedPO?.supplier_location_id
                        ? (locations.find(l => l.id === selectedPO.supplier_location_id)?.name || 'Unknown location')
                        : (selectedPO?.supplier?.name || 'No supplier')}
                    </span>
                  </div>
                </div>
                {/* Right column */}
                <div className="space-y-3">
                  <div className="flex">
                    <span className="w-44 font-semibold text-sm">Created</span>
                    <span className="text-sm">
                      {selectedPO ? new Date(selectedPO.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      }) : '-'}
                    </span>
                  </div>
                  <div className="flex">
                    <span className="w-44 font-semibold text-sm">Created by</span>
                    <span className="text-sm">{organization?.name || '-'}</span>
                  </div>
                  <div className="flex">
                    <span className="w-44 font-semibold text-sm">Delivery due</span>
                    <span className="text-sm">
                      {selectedPO?.expected_date
                        ? new Date(selectedPO.expected_date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : '-'}
                    </span>
                  </div>
                  <div className="flex">
                    <span className="w-44 font-semibold text-sm">Received</span>
                    <span className="text-sm">
                      {selectedPO?.received_date
                        ? new Date(selectedPO.received_date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : '-'}
                    </span>
                  </div>
                  <div className="flex">
                    <span className="w-44 font-semibold text-sm">Supplier invoice</span>
                    <span className="text-sm">-</span>
                  </div>
                  <div className="flex">
                    <span className="w-44 font-semibold text-sm">Supplier invoice date</span>
                    <span className="text-sm">-</span>
                  </div>
                  {selectedPO?.notes && (
                    <div className="flex">
                      <span className="w-44 font-semibold text-sm">Note</span>
                      <span className="text-sm">{selectedPO.notes}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Subtotal and Order total */}
              <div className="space-y-1">
                <div className="flex gap-2">
                  <span className="font-semibold text-sm">Subtotal of products</span>
                  <span className="text-sm tabular-nums">{formatCurrency(selectedPO?.subtotal || 0)}</span>
                </div>
                <div className="flex gap-2">
                  <span className="font-semibold text-sm">Order total</span>
                  <span className="text-sm tabular-nums">{formatCurrency(selectedPO?.total || 0)}</span>
                </div>
              </div>

              {/* Items Table - Lightspeed style */}
              <div className="rounded-lg border overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b">
                    <tr>
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3 w-16">
                        Order
                      </th>
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">
                        Product
                      </th>
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3 w-24">
                        SKU
                      </th>
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3 w-28">
                        Supplier code
                      </th>
                      <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3 w-20">
                        Stock
                      </th>
                      <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3 w-20">
                        Ordered
                      </th>
                      <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3 w-20">
                        Received
                      </th>
                      <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3 w-28">
                        Cost price
                      </th>
                      <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3 w-28">
                        Total cost
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {selectedPOItems.map((item, idx) => {
                      // Get current stock for the product at the PO location
                      const productStock = item.product_id
                        ? (inventoryByProduct[item.product_id] || [])
                            .filter(inv => inv.location_id === selectedPO?.location_id)
                            .reduce((sum, inv) => sum + inv.quantity, 0)
                        : 0;

                      return (
                        <tr key={item.id} className="hover:bg-muted/20">
                          <td className="px-4 py-3 text-sm text-muted-foreground">
                            {idx + 1}
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-medium text-sm text-primary">{item.product?.name || 'Unknown'}</p>
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">
                            {item.product?.sku || ''}
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">
                            {/* Supplier code - uses SKU as fallback if no dedicated supplier_code field */}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-sm">
                            {productStock}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-sm">
                            {item.quantity_ordered}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-sm">
                            <span className={cn(
                              item.quantity_received >= item.quantity_ordered
                                ? 'text-green-600 font-medium'
                                : item.quantity_received > 0
                                ? 'text-amber-600 font-medium'
                                : 'text-muted-foreground'
                            )}>
                              {item.quantity_received}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-sm">
                            {formatCurrency(item.unit_cost)}
                          </td>
                          <td className="px-4 py-3 text-right font-medium tabular-nums text-sm">
                            {formatCurrency(item.quantity_ordered * item.unit_cost)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {/* Highlighted totals row */}
                  <tfoot>
                    <tr className="bg-amber-50 dark:bg-amber-950/30 font-medium">
                      <td colSpan={5} className="px-4 py-3 text-sm font-semibold">Total</td>
                      <td className="px-4 py-3 text-right tabular-nums text-sm font-semibold">
                        {selectedPOItems.reduce((sum, item) => sum + item.quantity_ordered, 0)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-sm font-semibold">
                        {selectedPOItems.reduce((sum, item) => sum + item.quantity_received, 0)}
                      </td>
                      <td></td>
                      <td className="px-4 py-3 text-right tabular-nums text-sm font-semibold">
                        {formatCurrency(selectedPOItems.reduce((sum, item) => sum + (item.quantity_ordered * item.unit_cost), 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Receive Items Dialog */}
      <ResponsiveModal open={receiveDialogOpen} onOpenChange={setReceiveDialogOpen}>
        <ResponsiveModalContent maxWidth="md">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Receive Items</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              Enter quantities being received now for {selectedPO?.order_number}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody>
            <div className="space-y-3">
              {selectedPOItems.map((item) => {
                const remaining = item.quantity_ordered - item.quantity_received;
                const isFullyReceived = remaining <= 0;
                return (
                  <div key={item.id} className={cn(
                    "flex items-center gap-4 p-3 rounded-lg border",
                    isFullyReceived && "bg-muted/50 opacity-60"
                  )}>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{item.product?.name}</p>
                      <p className="text-xs text-muted-foreground">
                        Ordered: {item.quantity_ordered} • Received: {item.quantity_received} • 
                        <span className={remaining > 0 ? "text-amber-600 font-medium" : "text-green-600"}>
                          {remaining > 0 ? ` Remaining: ${remaining}` : " Fully received"}
                        </span>
                      </p>
                    </div>
                    <div className="w-24">
                      <Input
                        type="number"
                        min="0"
                        max={remaining}
                        value={receiveQuantities[item.id] || ''}
                        onChange={(e) => setReceiveQuantities({
                          ...receiveQuantities,
                          [item.id]: e.target.value,
                        })}
                        placeholder={isFullyReceived ? "Done" : `0-${remaining}`}
                        disabled={isFullyReceived}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setReceiveDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="blaze"
              onClick={handleReceiveItems}
              disabled={receiveItems.isPending}
            >
              {receiveItems.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Confirm Receipt
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Email to Supplier Dialog */}
      <ResponsiveModal open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <ResponsiveModalContent maxWidth="sm">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Email Purchase Order</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              Send {selectedPO?.order_number} to supplier via email
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody>
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-muted/50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Order</span>
                  <span className="font-medium">{selectedPO?.order_number}</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Supplier</span>
                  <span className="font-medium">
                    {selectedPO?.supplier_location_id
                      ? (locations.find(l => l.id === selectedPO.supplier_location_id)?.name || 'Unknown location')
                      : (selectedPO?.supplier?.name || 'No supplier')}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total</span>
                  <span className="font-semibold">{formatCurrency(selectedPO?.total || 0)}</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email-recipient">Recipient Email</Label>
                <Input
                  id="email-recipient"
                  type="email"
                  value={emailRecipient}
                  onChange={(e) => setEmailRecipient(e.target.value)}
                  placeholder="supplier@example.com"
                />
                <p className="text-xs text-muted-foreground">
                  Leave empty to use the supplier's email address
                </p>
              </div>
            </div>
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="blaze"
              onClick={() => selectedPO && sendPOEmail.mutateAsync({
                poId: selectedPO.id,
                recipient: emailRecipient || undefined,
              })}
              disabled={sendPOEmail.isPending}
            >
              {sendPOEmail.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              <Mail className="h-4 w-4 mr-2" />
              Send Email
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Edit PO Dialog - QuickBooks Style */}
      <ResponsiveModal open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <ResponsiveModalContent maxWidth="5xl">
          <form onSubmit={handleUpdatePO}>
            {/* Header */}
            <div className="shrink-0 border-b px-6 py-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <Edit2 className="h-6 w-6 text-muted-foreground" />
                  <div>
                    <h2 className="text-xl font-semibold">Edit Purchase Order {selectedPO?.order_number}</h2>
                    <div className="flex items-center gap-2 mt-1">
                      {selectedPO && (
                        <Badge className={cn('gap-1', STATUS_CONFIG[selectedPO.status].color)}>
                          {STATUS_CONFIG[selectedPO.status].label}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Current Total</p>
                  <p className="text-2xl font-bold text-primary tabular-nums">
                    {formatCurrency(formItems.reduce((sum, item) => {
                      const qty = parseFloat(item.quantity) || 0;
                      const cost = parseFloat(item.unit_cost) || 0;
                      return sum + qty * cost;
                    }, 0))}
                  </p>
                </div>
              </div>
            </div>

            <ResponsiveModalBody className="max-h-[70vh] overflow-y-auto">
              <div className="space-y-6">
                {formError && (
                  <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-950/20 rounded-lg">
                    {formError}
                  </div>
                )}

                {/* Info Grid - QuickBooks style */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-6 p-4 bg-muted/30 rounded-lg">
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide">Deliver to</Label>
                    <p className="font-medium mt-1">{selectedPO?.location?.name}</p>
                  </div>

                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide">Order from</Label>
                    <p className="font-medium mt-1">
                      {formData.order_from_type === 'supplier' ? 'External supplier' : 'Internal location'}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                      {formData.order_from_type === 'supplier' ? 'Supplier' : 'Internal location'}
                    </Label>
                    {formData.order_from_type === 'supplier' ? (
                      <Select
                        value={formData.supplier_id}
                        onValueChange={(value) => {
                          setFormData({ ...formData, supplier_id: value });
                          setTimeout(() => checkSupplierMismatch(formItems), 0);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select supplier" />
                        </SelectTrigger>
                        <SelectContent>
                          {suppliers.map((supplier) => (
                            <SelectItem key={supplier.id} value={supplier.id}>
                              {supplier.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Select
                        value={formData.supplier_location_id}
                        onValueChange={(value) => setFormData({ ...formData, supplier_location_id: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select internal location" />
                        </SelectTrigger>
                        <SelectContent>
                          {locations.map((location) => (
                            <SelectItem key={location.id} value={location.id}>
                              {location.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide">Order Date</Label>
                    <p className="font-medium mt-1">
                      {selectedPO ? new Date(selectedPO.created_at).toLocaleDateString() : '-'}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide">Expected Delivery</Label>
                    <Input
                      type="date"
                      value={formData.expected_date}
                      onChange={(e) => setFormData({ ...formData, expected_date: e.target.value })}
                    />
                  </div>
                </div>

                {/* Shipping Address */}
                <div className="p-4 bg-muted/30 rounded-lg space-y-4">
                  <Label className="text-sm font-medium">Shipping Address</Label>
                  <p className="text-xs text-muted-foreground -mt-2">Where should the supplier ship the items?</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground uppercase tracking-wide">Address Line 1</Label>
                      <Input
                        value={formData.shipping_address.line1}
                        onChange={(e) => setFormData({
                          ...formData,
                          shipping_address: { ...formData.shipping_address, line1: e.target.value }
                        })}
                        placeholder="Street address"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground uppercase tracking-wide">Address Line 2</Label>
                      <Input
                        value={formData.shipping_address.line2}
                        onChange={(e) => setFormData({
                          ...formData,
                          shipping_address: { ...formData.shipping_address, line2: e.target.value }
                        })}
                        placeholder="Suite, unit, building (optional)"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground uppercase tracking-wide">City</Label>
                      <Input
                        value={formData.shipping_address.city}
                        onChange={(e) => setFormData({
                          ...formData,
                          shipping_address: { ...formData.shipping_address, city: e.target.value }
                        })}
                        placeholder="City"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground uppercase tracking-wide">State / Province</Label>
                      <Input
                        value={formData.shipping_address.state}
                        onChange={(e) => setFormData({
                          ...formData,
                          shipping_address: { ...formData.shipping_address, state: e.target.value }
                        })}
                        placeholder="State or province"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground uppercase tracking-wide">Postal Code</Label>
                      <Input
                        value={formData.shipping_address.postal_code}
                        onChange={(e) => setFormData({
                          ...formData,
                          shipping_address: { ...formData.shipping_address, postal_code: e.target.value }
                        })}
                        placeholder="ZIP / Postal code"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground uppercase tracking-wide">Country</Label>
                      <Input
                        value={formData.shipping_address.country}
                        onChange={(e) => setFormData({
                          ...formData,
                          shipping_address: { ...formData.shipping_address, country: e.target.value }
                        })}
                        placeholder="Country"
                      />
                    </div>
                  </div>
                </div>

                {/* Line Items Table */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-sm font-medium">Line Items</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addFormItem}>
                      <Plus className="h-3 w-3 mr-1" />
                      Add Line
                    </Button>
                  </div>

                  <div className="rounded-lg border overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-muted/50 border-b">
                        <tr>
                          <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3 w-10">
                            #
                          </th>
                          <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">
                            Product / Service
                          </th>
                          <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">
                            Description
                          </th>
                          <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3 w-24">
                            Qty
                          </th>
                          <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3 w-28">
                            Rate
                          </th>
                          <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3 w-28">
                            Amount
                          </th>
                          <th className="w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {formItems.map((item, index) => {
                          const qty = parseFloat(item.quantity) || 0;
                          const rate = parseFloat(item.unit_cost) || 0;
                          const amount = qty * rate;
                          return (
                            <tr key={index} className="hover:bg-muted/20">
                              <td className="px-4 py-2 text-sm text-muted-foreground">
                                {index + 1}
                              </td>
                              <td className="px-4 py-2">
                                <Select
                                  value={item.product_id}
                                  onValueChange={(value) => updateFormItem(index, 'product_id', value)}
                                >
                                  <SelectTrigger className="border-0 bg-transparent shadow-none h-8 px-2 focus:ring-1">
                                    <SelectValue placeholder="Select product" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {productsWithSupplier.map((product) => (
                                      <SelectItem key={product.id} value={product.id}>
                                        {product.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="px-4 py-2">
                                <Input
                                  placeholder="Description"
                                  value={item.description}
                                  onChange={(e) => updateFormItem(index, 'description', e.target.value)}
                                  className="border-0 bg-transparent shadow-none h-8 px-2 focus:ring-1"
                                />
                              </td>
                              <td className="px-4 py-2">
                                <Input
                                  type="number"
                                  min="1"
                                  placeholder="0"
                                  value={item.quantity}
                                  onChange={(e) => updateFormItem(index, 'quantity', e.target.value)}
                                  className="border-0 bg-transparent shadow-none h-8 px-2 text-right focus:ring-1"
                                />
                              </td>
                              <td className="px-4 py-2">
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="0.00"
                                  value={item.unit_cost}
                                  onChange={(e) => updateFormItem(index, 'unit_cost', e.target.value)}
                                  className="border-0 bg-transparent shadow-none h-8 px-2 text-right focus:ring-1"
                                />
                              </td>
                              <td className="px-4 py-2 text-right font-medium tabular-nums text-sm">
                                {formatCurrency(amount)}
                              </td>
                              <td className="px-2 py-2">
                                {formItems.length > 1 && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-muted-foreground hover:text-red-600"
                                    onClick={() => removeFormItem(index)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Totals Section */}
                <div className="flex justify-end">
                  <div className="w-72 space-y-2">
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span className="font-medium tabular-nums">
                        {formatCurrency(formItems.reduce((sum, item) => {
                          const qty = parseFloat(item.quantity) || 0;
                          const cost = parseFloat(item.unit_cost) || 0;
                          return sum + qty * cost;
                        }, 0))}
                      </span>
                    </div>
                    <div className="flex justify-between py-3 border-b-2 border-primary">
                      <span className="font-semibold">Total</span>
                      <span className="font-bold text-lg text-primary tabular-nums">
                        {formatCurrency(formItems.reduce((sum, item) => {
                          const qty = parseFloat(item.quantity) || 0;
                          const cost = parseFloat(item.unit_cost) || 0;
                          return sum + qty * cost;
                        }, 0))}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wide">Notes / Memo</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes for this purchase order..."
                    rows={2}
                    className="bg-background"
                  />
                </div>
              </div>
            </ResponsiveModalBody>

            <ResponsiveModalFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="blaze" disabled={updatePO.isPending} className="gap-2">
                {updatePO.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                <Save className="h-4 w-4" />
                Save Changes
              </Button>
            </ResponsiveModalFooter>
          </form>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Convert to Invoice Dialog */}
      <ResponsiveModal open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <ResponsiveModalContent maxWidth="md">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Convert to Invoice
            </ResponsiveModalTitle>
            <ResponsiveModalDescription>
              Create a supplier invoice from this purchase order
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody>
            <div className="space-y-4">
              {/* PO Summary */}
              <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Purchase Order</span>
                  <span className="font-medium">{selectedPO?.order_number}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Supplier</span>
                  <span className="font-medium">{selectedPO?.supplier?.name || 'No supplier'}</span>
                </div>
                <div className="flex items-center justify-between border-t pt-2 mt-2">
                  <span className="text-sm font-medium">Total Amount</span>
                  <span className="font-bold text-primary">{formatCurrency(selectedPO?.total || 0)}</span>
                </div>
              </div>

              {/* Invoice Number */}
              <div className="space-y-2">
                <Label htmlFor="invoice-number">Invoice Number (Optional)</Label>
                <Input
                  id="invoice-number"
                  value={convertInvoiceNumber}
                  onChange={(e) => setConvertInvoiceNumber(e.target.value)}
                  placeholder={`${selectedPO?.order_number || 'PO'}-INV`}
                />
                <p className="text-xs text-muted-foreground">
                  Leave blank to auto-generate based on PO number
                </p>
              </div>

              {/* Info */}
              <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  This will create a supplier invoice with all received items from this purchase order.
                  The invoice can be used for tracking payments and accounting purposes.
                </p>
              </div>
            </div>
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConvertDialogOpen(false);
                setConvertInvoiceNumber('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="blaze"
              className="gap-2"
              onClick={() => {
                if (selectedPO) {
                  convertToInvoice.mutate({
                    poId: selectedPO.id,
                    invoiceNumber: convertInvoiceNumber || undefined,
                  });
                }
              }}
              disabled={convertToInvoice.isPending}
            >
              {convertToInvoice.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Receipt className="h-4 w-4" />
              )}
              Create Invoice
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>
    </div>
  );
}
